import React, {
  FC,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useDeferredValue,
  useMemo,
} from 'react';
import {
  belowBreakpoint,
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Heading,
  Icon,
  IconSize,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldSize,
  Theme,
  useMediaQuery,
  useModalState,
  useSideSheetState,
} from '@bechtel/gds';
import {
  CommodityMilestone,
  CommodityType,
  MaterialCommodity,
  MilestoneStatusCode,
  MilestoneType,
  OwnershipLevel,
  PagingRowNumberInput,
  PersonaType,
  SortOrder,
  WeeklyWorkInput,
  WeeklyWorkList,
  WeeklyWorkListBulkItem,
  WeeklyWorkListSearchBy,
  WeeklyWorkListSortByTerm,
  WeeklyWorkSummary,
  Weld,
} from '@bechtel/types';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { endOfWeek, formatISO, getTime, parseISO } from 'date-fns';
import { filter, find, get, isEqual, map } from 'lodash';
import { ThemeContext } from 'styled-components';
import {
  useSessionStorage,
  useDate,
  SuspenseLoader,
  useAppUser,
  SearchField,
  ErrorBoundary,
  CacheKeys,
} from '@bechtel/client-common';
import { GET_WORKLIST_DATA, GET_WORKLIST_SUMMARY_STATS } from '@bechtel/client-gql';
//@ts-ignore declaration not recognizing svg
import { ReactComponent as SearchResults } from '@bechtel/client-assets/dist/images/search-results.svg';
import { isWorkPackagePersona } from '~/utilities';
import { WorkListMenuType } from '../worklist-detail-button/types';
import { DownloadWorkList } from '../worklist-download';
import { searchByList } from '../worklist-drawer/drawer-state';
import { WeeklyWorklistItem } from './item';
import { MilestoneFilters } from './milestone-filters';
import {
  ForemanWorklistStyled,
  SearchCanvasButtonStyled,
  SearchFieldStyled,
  SearchIconStyled,
  WorklistBodyStyled,
  WorklistGroupStyled,
  WorklistHeaderStyled,
} from './styled';
import { TabFilters } from './tab-filters';
import { WeeklyWorklistProps, WorkListDrawerValueProps, WorkListSelectionStateProps } from './types';
import { WeekStartType } from '~/types';
import { WorkListDrawer } from '../worklist-drawer';
import { CommodityDetail } from '@bechtel/client-work-packages';
import { CreateBlock } from '@bechtel/client-blocks';
import { AddViewAttachment } from '~/add-view-attachment';
import { t, Trans } from '@lingui/macro';
import { useBulkWeeklyWorklist } from '~/hooks/use-bulk-weekly-worklist';

const initialDrawerValues: WorkListDrawerValueProps = {
  searchBy: WeeklyWorkListSearchBy.TAG_ID,
  filterByCommodity: [],
  filterByMilestoneTypes: [],
  filterByPlanStatusTypes: [],
  sortBy: WeeklyWorkListSortByTerm.TAG,
  sortDir: SortOrder.DESC,
};

export const WeeklyWorklist: FC<WeeklyWorklistProps> = ({ showDownload, showOwners, crewMember }) => {
  const { user } = useAppUser();
  const { getWeekNo } = useDate();
  const [downloadDataReady, setDownloadDataReady] = useState<boolean>(false);
  const [downloadStarted, setDownloadStarted] = useState<boolean>(false);
  const [updatedMilestoneId, setUpdatedMilestoneId] = useState<string>();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(belowBreakpoint(breakpoints.widescreen));
  const { close, toggle, visible } = useModalState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCommodity, setSelectedCommodity] = useState<MaterialCommodity | Weld>();
  const [selectedMilestone, setSelectedMilestone] = useState<CommodityMilestone>();
  const bulkPageLimit = 10;
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  //@ts-ignore ignore undefined tupe warning as commodity wrapper will either have a weld or material commodity always
  const milestoneCommodity: MaterialCommodity | Weld = selectedMilestone?.commodity.materialCommodity
    ? selectedMilestone?.commodity.materialCommodity
    : selectedMilestone?.commodity.weld;

  const assignedCrewMembers = milestoneCommodity?.workPackage
    ? [
        ...milestoneCommodity.workPackage.primaryOwners,
        ...milestoneCommodity.workPackage.secondaryOwners,
        ...milestoneCommodity.workPackage.otherOwners,
      ]
    : [];

  const {
    visible: visibleCommodityDetail,
    open: openCommodityDetailSideSheet,
    close: closeCommodityDetailSideSheet,
  } = useSideSheetState(false);
  const { visible: visibleCreateBlock, open: openCreateBlock, close: closeCreateBlock } = useSideSheetState(false);
  const {
    visible: visibleMilestoneAttachment,
    open: openMilestoneAttachment,
    close: closeMilestoneAttachment,
  } = useSideSheetState(false);

  const debouncedSearchQuery = useDeferredValue(searchQuery?.trim());

  const weekStartsOn: WeekStartType = (user?.project?.projectConfiguration.weekStartDay
    ? user?.project?.projectConfiguration.weekStartDay
    : 1) as WeekStartType; // Default start day is monday
  const pageLimit = 15;

  const persistedDrawerValues = useRef(initialDrawerValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<WorkListDrawerValueProps>(
    CacheKeys.WorkListDrawerValues,
    persistedDrawerValues.current || initialDrawerValues
  );
  const [selectionStateValues, setSelectionStateValues] = useSessionStorage<WorkListSelectionStateProps>(
    CacheKeys.WorkListSelectionStateValues
  );
  const claimedFilter = selectionStateValues?.milestoneStatus === MilestoneStatusCode.CLAIMED ? true : false;
  const selectedWeekStartDay = selectionStateValues?.weekStartDay
    ? selectionStateValues?.weekStartDay
    : currentProjectWeek?.startDate;
  //The below conversion is required as date-fns do not accept string.
  //This convertion cannot be implemented in selectedWeekStartDay and currentWeekDay as they are used as dependencies in useEffect and converting will lead to infinite re-render.
  const selectedWeekStartDate =
    selectedWeekStartDay instanceof Date ? selectedWeekStartDay : parseISO(selectedWeekStartDay);
  const selectedWeek = getWeekNo(selectedWeekStartDate, weekStartsOn);
  //Convert to number so the useEffect will not run when only week is changed in selected state values
  const selectedWeekStartTime = getTime(selectedWeekStartDate);
  const [loadWeeklyWorkList, { loading, data, error, refetch, networkStatus, called, fetchMore }] = useLazyQuery<
    {
      weeklyWorkList: WeeklyWorkList;
    },
    {
      input: WeeklyWorkInput;
      paging?: PagingRowNumberInput;
    }
  >(GET_WORKLIST_DATA, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const [
    loadWorklistSummary,
    { loading: loadingSummary, data: summaryData, error: summaryError, refetch: summaryRefetch },
  ] = useLazyQuery<
    {
      weeklyWorkList: WeeklyWorkSummary;
    },
    {
      input: WeeklyWorkInput;
    }
  >(GET_WORKLIST_SUMMARY_STATS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const searchLabel = find(searchByList, (searchByItem) => {
    return searchByItem.searchByTerm.type === drawerValues.searchBy;
  })?.label;

  const weeklyWorkListItems = useMemo(() => {
    return get(data, 'weeklyWorkList.items', []);
  }, [data]);
  const workSummary: WeeklyWorkSummary = get(summaryData, 'weeklyWorkSummary');
  const currentRowNumber = get(data, 'weeklyWorkList.rowNumber', 0);
  const totalRecords = get(data, 'weeklyWorkList.total', 0);

  const filteredWorkListItems = filter(weeklyWorkListItems, (item: CommodityMilestone) => {
    if (claimedFilter) {
      return (
        item.currentStatus?.codeI18n === MilestoneStatusCode.CLAIMED ||
        item.currentStatus?.codeI18n === MilestoneStatusCode.APPROVED
      );
    }

    return (
      item.currentStatus?.codeI18n === MilestoneStatusCode.PLANNED ||
      item.currentStatus?.codeI18n === MilestoneStatusCode.UNCLAIMED ||
      item.currentStatus?.codeI18n === MilestoneStatusCode.REJECTED ||
      item.currentStatus?.codeI18n === MilestoneStatusCode.BLOCKED
    );
  });

  useEffect(() => {
    if (
      (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3) ||
      !crewMember?.id ||
      !crewMember?.persona ||
      !isWorkPackagePersona(crewMember?.persona) ||
      !currentProjectWeek?.startDate
    )
      return;
    const weekStartDate = new Date(selectedWeekStartTime);
    const worklistInput: WeeklyWorkInput = {
      crewMemberId: crewMember?.id,
      personaType: crewMember?.persona,
      startDate: formatISO(weekStartDate, { representation: 'date' }),
      endDate: formatISO(endOfWeek(weekStartDate, { weekStartsOn: weekStartsOn }), { representation: 'date' }),
      searchValue: debouncedSearchQuery,
      ownershipLevel: OwnershipLevel.PRIMARY, //TODO : Implement Secondary other once backend supports it
      workPackageIds: [],
      searchByField: drawerValues?.searchBy ? drawerValues?.searchBy : WeeklyWorkListSearchBy.TAG_ID,
      filterByMilestoneTypes: map(
        drawerValues?.filterByMilestoneTypes,
        (milestoneType: MilestoneType) => milestoneType.id
      ),
      filterByCommodityTypes: map(drawerValues?.filterByCommodity, (item: CommodityType) => item.id),
      filterByMilestoneStatus: claimedFilter ? MilestoneStatusCode.CLAIMED : MilestoneStatusCode.PLANNED,
      filterByPlanStatus: map(drawerValues?.filterByPlanStatusTypes, (item) => item.value),
      sortBy: drawerValues?.sortBy ? drawerValues?.sortBy : WeeklyWorkListSortByTerm.TAG,
      sortOrder: drawerValues?.sortDir ? drawerValues?.sortDir : SortOrder.DESC,
    };
    loadWeeklyWorkList({
      variables: {
        input: worklistInput,
        paging: {
          rowNumber: 0,
          limit: pageLimit,
        },
      },
    });
  }, [
    loadWeeklyWorkList,
    loadWorklistSummary,
    weekStartsOn,
    drawerValues,
    debouncedSearchQuery,
    crewMember,
    selectedWeekStartTime,
    claimedFilter,
  ]);

  const weekStartDate = new Date(selectedWeekStartTime);
  const weeklyWorkInput: WeeklyWorkInput = {
    crewMemberId: crewMember?.id ? crewMember?.id : '',
    personaType: crewMember?.persona ? crewMember?.persona : PersonaType.NONE,
    startDate: currentProjectWeek?.startDate ? formatISO(weekStartDate, { representation: 'date' }) : '',
    endDate: currentProjectWeek?.startDate
      ? formatISO(endOfWeek(weekStartDate, { weekStartsOn: weekStartsOn }), { representation: 'date' })
      : '',
    searchValue: debouncedSearchQuery,
    ownershipLevel: OwnershipLevel.PRIMARY, //TODO : Implement Secondary other once backend supports it
    workPackageIds: [],
    searchByField: drawerValues?.searchBy ? drawerValues?.searchBy : WeeklyWorkListSearchBy.TAG_ID,
    filterByMilestoneTypes: map(
      drawerValues?.filterByMilestoneTypes,
      (milestoneType: MilestoneType) => milestoneType.id
    ),
    filterByCommodityTypes: map(drawerValues?.filterByCommodity, (item: CommodityType) => item.id),
    filterByMilestoneStatus: claimedFilter ? MilestoneStatusCode.CLAIMED : MilestoneStatusCode.PLANNED,
    filterByPlanStatus: map(drawerValues?.filterByPlanStatusTypes, (item) => item.value),
    sortBy: drawerValues?.sortBy ? drawerValues?.sortBy : WeeklyWorkListSortByTerm.TAG,
    sortOrder: drawerValues?.sortDir ? drawerValues?.sortDir : SortOrder.DESC,
  };

  useEffect(() => {
    if (
      (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3) ||
      !crewMember?.id ||
      !crewMember?.persona ||
      !isWorkPackagePersona(crewMember?.persona) ||
      !currentProjectWeek?.startDate
    )
      return;
    loadWorklistSummary({
      variables: {
        input: weeklyWorkInput,
      },
    });
  }, [
    loadWorklistSummary,
    weekStartsOn,
    drawerValues,
    debouncedSearchQuery,
    crewMember,
    selectedWeekStartTime,
    weeklyWorkListItems,
  ]);

  const onDrawerSubmit = useCallback(
    (values: WorkListDrawerValueProps) => {
      setDrawerValues(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setDrawerValues]
  );
  const fetchResult = async (actioned: boolean = false) => {
    const { data } = await fetchMore({
      variables: {
        paging: {
          rowNumber: actioned && filteredWorkListItems ? filteredWorkListItems.length - 1 : currentRowNumber,
          limit: pageLimit,
        },
      },
    });
    return get(data, 'weeklyWorkList.rowNumber', 0);
  };

  const handlScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      currentRowNumber < totalRecords &&
      !loading
    ) {
      fetchResult();
    }
  };

  const handleActionCompleted = () => {
    if (filteredWorkListItems && filteredWorkListItems.length <= pageLimit && currentRowNumber < totalRecords)
      fetchResult(true);
  };

  const onWorkListMenuSelect = useCallback(
    (workListMenuType: WorkListMenuType, milestone: CommodityMilestone) => {
      //@ts-ignore Ignore undefined tupe warning as commodity wrapper will either
      //have a weld or material commodity always
      const commodity: MaterialCommodity | Weld = milestone.commodity.materialCommodity
        ? milestone.commodity.materialCommodity
        : milestone.commodity.weld;
      if (workListMenuType === WorkListMenuType.COMMODITY_DETAIL) {
        setSelectedCommodity(commodity);
        openCommodityDetailSideSheet();
      } else if (workListMenuType === WorkListMenuType.CREATE_BLOCK) {
        setSelectedMilestone(milestone);
        openCreateBlock();
      } else if (workListMenuType === WorkListMenuType.ADD_VIEW_PHOTOS) {
        setSelectedMilestone(milestone);
        openMilestoneAttachment();
      }
    },
    [openCommodityDetailSideSheet, openCreateBlock, openMilestoneAttachment]
  );

  const [
    loadBulkWorklistData,
    { loading: bulkLoading, data: bulkData, fetchMore: bulkFetchMore },
  ] = useBulkWeeklyWorklist(true);
  const bulkWorklistItems: WeeklyWorkListBulkItem[] = get(bulkData, 'weeklyWorkListBulk.items', []);
  let currentBulkRowNumber = get(bulkData, 'weeklyWorkListBulk.rowNumber', 0);
  const totalBulkRecords = get(bulkData, 'weeklyWorkListBulk.total', 0);

  const fetchBulkResult = async () => {
    const { data: bulkData } = await bulkFetchMore({
      variables: {
        paging: {
          rowNumber: currentBulkRowNumber,
          limit: bulkPageLimit,
        },
      },
    });
    return get(bulkData, 'weeklyWorkListBulk.rowNumber', 0);
  };

  const downloadWWL = async () => {
    currentBulkRowNumber = 0;
    setDownloadStarted(true);
    if (downloadDataReady) {
      setDownloadDataReady(false);
    }
    await loadBulkWorklistData({
      variables: {
        input: weeklyWorkInput,
        paging: {
          rowNumber: currentBulkRowNumber,
          limit: bulkPageLimit,
        },
      },
    });
  };

  const getAllBulkRecords = async () => {
    let hasNextPage = currentBulkRowNumber < totalBulkRecords;
    while (hasNextPage && !bulkLoading) {
      currentBulkRowNumber = await fetchBulkResult();
      hasNextPage = currentBulkRowNumber < totalBulkRecords;
    }
    if (!hasNextPage && totalBulkRecords) {
      setDownloadDataReady(true);
      setDownloadStarted(false);
    }
  };

  useEffect(() => {
    getAllBulkRecords();
  }, [bulkData, currentBulkRowNumber, downloadDataReady]);

  return (
    <ForemanWorklistStyled>
      <WorklistHeaderStyled
        alignItems={{
          sm: 'center',
        }}
      >
        <Heading style={{ flex: 1 }} size={isWideScreen ? '500' : '600'} weight="600">
          <Trans>Weekly Work List</Trans>
        </Heading>
        <SearchFieldStyled>
          <SearchField
            size={TextFieldSize.SMALL}
            iconSize={IconSize.SMALL}
            placeholder={t`Search ${searchLabel}s`} //There will atleast one search by selected always
            type="search"
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            value={searchQuery}
          />
        </SearchFieldStyled>
        <Box mr={{ sm: '400' }}>
          <SearchCanvasButtonStyled
            size={ButtonSize.SMALL}
            appearance={ButtonAppearance.SECONDARY}
            isDefaultFilterChange={!isEqual(drawerValues, initialDrawerValues)}
            onClick={toggle}
          >
            <SearchIconStyled
              icon="sliders-v"
              size={IconSize.MEDIUM}
              fillColor={!isEqual(drawerValues, initialDrawerValues) ? 'white' : ''}
            />
          </SearchCanvasButtonStyled>
        </Box>
        {showDownload && (
          <DownloadWorkList
            milestoneStatus={selectionStateValues?.milestoneStatus}
            isWWLDownload={true}
            downloadWWL={downloadWWL}
            loading={bulkLoading || downloadStarted}
            workListItems={bulkWorklistItems}
            downloadDataReady={downloadDataReady}
            setDownloadDataReady={setDownloadDataReady}
            selectedWeek={selectedWeek}
            disabled={filteredWorkListItems?.length === 0 || bulkLoading || downloadStarted}
          />
        )}
        <Box>
          <Button size={ButtonSize.SMALL} appearance={ButtonAppearance.SECONDARY} disabled={true}>
            <Icon icon="print" size={IconSize.MEDIUM} fillColor="blue.800" />
          </Button>
        </Box>
      </WorklistHeaderStyled>
      {crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona) && (
        <TabFilters
          user={user}
          onTabFilterChange={setSelectionStateValues}
          tabFilterValues={selectionStateValues}
          loading={loadingSummary}
          workSummary={workSummary}
        ></TabFilters>
      )}
      <WorklistBodyStyled flexDirection={{ sm: 'row' }}>
        {crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona) ? (
          <>
            <MilestoneFilters
              selectionStateValues={selectionStateValues}
              user={user}
              drawerValues={drawerValues}
              onChange={(data) => {
                setDrawerValues({ ...drawerValues, filterByMilestoneTypes: data });
              }}
              crewMember={crewMember}
              searchQuery={searchQuery}
              weeklyWorkListItems={weeklyWorkListItems}
            ></MilestoneFilters>
            <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
              <WorklistGroupStyled
                data-testid="worklist"
                onScroll={(e) => {
                  handlScroll(e);
                }}
              >
                {map(filteredWorkListItems, (item) => {
                  return (
                    <ErrorBoundary>
                      <WeeklyWorklistItem
                        item={item}
                        showOwners={showOwners}
                        key={item.id}
                        isOutOfPlan={
                          claimedFilter &&
                          (!item.plannedStartDate ||
                            selectedWeek !== getWeekNo(parseISO(item.plannedStartDate), weekStartsOn))
                        }
                        user={user}
                        crewMember={crewMember}
                        onWorkListMenuSelect={(workListMenuType) => {
                          onWorkListMenuSelect(workListMenuType, item);
                        }}
                        onActionUpdating={() => {
                          setUpdatedMilestoneId(item.id);
                        }}
                        onActionCompleted={() => {
                          handleActionCompleted();
                          setUpdatedMilestoneId('');
                        }}
                        disableAction={updatedMilestoneId && updatedMilestoneId !== item.id ? true : false}
                      />
                    </ErrorBoundary>
                  );
                })}
              </WorklistGroupStyled>

              {!(loading || loadingSummary) && (error || summaryError) && (
                <Box
                  alignItems={{ sm: 'center' }}
                  justifyContent={{ sm: 'center' }}
                  flexGrow={{ sm: 1 }}
                  flexDirection={{ sm: 'column' }}
                  p={{ sm: '500' }}
                >
                  <Box pb={{ sm: '500' }}>
                    <Text>
                      <Trans>
                        There was a problem loading weekly work list items. Please click the button below to try again.
                      </Trans>
                    </Text>
                  </Box>
                  <Box>
                    <Button
                      onClick={() => {
                        refetch();
                        summaryRefetch();
                      }}
                    >
                      <Box mr={{ sm: '400' }}>
                        <Icon icon="undo" />
                      </Box>
                      <Trans>Retry</Trans>
                    </Button>
                  </Box>
                </Box>
              )}
              {!loading && !error && filteredWorkListItems?.length === 0 && (
                <Box
                  flexDirection={{ sm: 'column' }}
                  alignItems={{ sm: 'center' }}
                  justifyContent={{ sm: 'center' }}
                  style={{ width: '100%', height: '100%' }}
                >
                  <Heading size="400" weight="600" color="grey.600">
                    <Trans>No worklist items found.</Trans>
                  </Heading>
                  <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
                    <Text size="400" family="graphik" color="grey.600" style={{ textAlign: 'center' }}>
                      {claimedFilter ? (
                        <Trans>No claimed items available for the selected week!!!</Trans>
                      ) : (
                        <Trans>No planned items available for the selected week!!!</Trans>
                      )}
                    </Text>
                  </Box>
                </Box>
              )}
              {(!called || networkStatus === NetworkStatus.refetch || loading) && (
                <Box
                  alignItems={{ sm: 'center' }}
                  justifyContent={{ sm: 'center' }}
                  flexGrow={{ sm: 1 }}
                  p={{ sm: '500' }}
                  data-testid="loading"
                >
                  <Spinner size={SpinnerSize.LARGE} />
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Box
            flexDirection={{ sm: 'column' }}
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            flexGrow={{ sm: 1 }}
            data-testid="worklist-empty"
          >
            <SearchResults style={{ width: '124px', height: '110px' }} />
            <Heading size="400" weight="600" color="grey.600">
              <Trans>Nothing to show yet!</Trans>
            </Heading>
            <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
              <Text
                size="400"
                family="graphik"
                color="grey.600"
                //  line height is required because when sentence wraps in two lines gap between both lines are required.
                style={{ textAlign: 'center', lineHeight: '1.57' }}
              >
                <Trans>Once you select a user, you will be able to see all worklist items in this week plan.</Trans>
              </Text>
            </Box>
          </Box>
        )}
      </WorklistBodyStyled>
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visible && <WorkListDrawer close={close} initialValues={drawerValues} onSubmit={onDrawerSubmit} />}
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleCommodityDetail && selectedCommodity && (
            <CommodityDetail
              commodityId={selectedCommodity?.id}
              commodityName={selectedCommodity?.type.name}
              commodityTag={selectedCommodity?.tag}
              workPackageTag={selectedCommodity?.workPackage?.tag}
              close={closeCommodityDetailSideSheet}
              showDocuments={true}
            />
          )}
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleCreateBlock && selectedMilestone && (
            <CreateBlock
              workPackageId={milestoneCommodity.workPackage?.id}
              workPackageTag={milestoneCommodity.workPackage?.tag}
              assignedCrewMembers={assignedCrewMembers}
              weldCommodityAssignees={milestoneCommodity?.commodityAssignees}
              facilities={milestoneCommodity.workPackage?.facilities ? milestoneCommodity.workPackage?.facilities : []}
              selectedMilestone={selectedMilestone}
              multiBlock={false}
              close={closeCreateBlock}
              // @ts-ignore commodity conversion to commodity array
              commodities={[milestoneCommodity]}
            />
          )}
        </Suspense>
      </ErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleMilestoneAttachment && selectedMilestone && (
            <AddViewAttachment milestone={selectedMilestone} closeModal={closeMilestoneAttachment} />
          )}
        </Suspense>
      </ErrorBoundary>
    </ForemanWorklistStyled>
  );
};
